import React, { ReactElement, Ref, useContext, useRef } from 'react'
import { NotificationsLanguageModal } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import Heading, { HeadingLevel } from '../../Common/Components/Heading'
import Icon, { IconName } from '../../Common/Components/Icon'
import { IconSize } from '../../../NonReact/constants/React/Icon/Constants'
import ModalExitPromptContext, { ModalExitPromptType } from '../../Common/Components/Modal/ModalExitPromptContext'
import PopperBubble from '../../Common/Components/PopperBubble'
import { DocTopic } from '../../Common/Constants/Documentation'
import { LanguageContext } from '../../Common/Contexts'
import { CommonFormContext, FormContextType } from '../../Common/Contexts/CommonFormContext'
import { openHelpPortalByTopic } from '../../Common/Utils'
import { servletPath } from '../../../NonReact/common/RxJS/RxJS'
import { RoundedButton } from '../../../ReactRouting/Components/Atoms/RoundedButton/RoundedButton'

/**
 * Component properties type.
 */
type Props = {
    isValidationPopperOpen: boolean
    closeModal: () => void
    serverError?: string
}

/**
 * Footer actions for the Add User modal.
 *
 * @param isValidationPopperOpen - Is the validation popper open.
 * @param closeModal - Close modal function.
 * @param serverError Server error string.
 */
const FooterActions: React.FC<Props> = ({ isValidationPopperOpen, closeModal, serverError }: Props): ReactElement => {
    const language = useContext(LanguageContext) as CLanguageHandler
    const { displayExitPrompt }: ModalExitPromptType = useContext(ModalExitPromptContext)
    const commonForm: Nullable<FormContextType> = useContext(CommonFormContext)

    language.loadBundle(NotificationsLanguageModal.NAME)

    const elementRef: Ref<HTMLButtonElement> = useRef(null)

    const handleHelpClick = (): void => {
        openHelpPortalByTopic(servletPath, DocTopic.NOTIFICATIONS)
    }

    const handleCancel = (): void => {
        if (commonForm?.isTouched) {
            displayExitPrompt()
        } else {
            closeModal()
        }
    }

    return (
        <div>
            {serverError && (
                <div className='auf-server-error'>
                    <Icon iconName={IconName.ERRORCIRCLE} iconSize={IconSize.LARGE} />
                    <div>
                        <Heading level={HeadingLevel.TWO}>
                            {language.get(NotificationsLanguageModal.NotificationFooterValidationError)}
                        </Heading>
                        <p>{serverError}</p>
                    </div>
                </div>
            )}
            <div className='tabs-footer'>
                <RoundedButton onClick={handleHelpClick} shrink>
                    {language.get(NotificationsLanguageModal.NotificationsFooterHelp)}
                </RoundedButton>
                <RoundedButton popoverRef={elementRef} type='submit' shrink>
                    {language.get(NotificationsLanguageModal.NotificationsFooterSubmit)}
                </RoundedButton>

                <PopperBubble
                    anchorEl={elementRef.current}
                    open={isValidationPopperOpen}
                    placement='top'
                    content={
                        <div className='validation-popper-container'>
                            <Icon iconName={IconName.ERRORCIRCLE} />
                            <span>{language.get(NotificationsLanguageModal.NotificationsFooterValiationWarning)}</span>
                        </div>
                    }
                    className='colour-red'
                />

                <RoundedButton onClick={handleCancel} shrink>
                    {language.get(NotificationsLanguageModal.NotificationsFooterCancel)}
                </RoundedButton>
            </div>
        </div>
    )
}

export default FooterActions
